import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { useLanguage } from "@/context/LanguageContext";
import { SOLUTIONS } from "@/data/solutions";
import { SEO_PAGES } from "@/data/seoPages";
import { WA_DISPLAY, waLink } from "@/lib/api";

export const Footer = () => {
  const { t, lang } = useLanguage();

  return (
    <footer className="border-t border-coal bg-ink" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo-link">
              <LogoMark className="h-12 w-10 shrink-0" />
              <span className="leading-none">
                <span className="block text-[10px] uppercase tracking-[0.3em] text-ivory/60">Scutul</span>
                <span className="block font-serif text-2xl text-ivory">Fermierului</span>
              </span>
            </Link>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-ivory/60">{t.footer.tagline}</p>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-bronze">{t.footer.navTitle}</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link data-testid="footer-nav-home" to="/" className="text-ivory/60 transition-colors hover:text-ivory">{t.nav.home}</Link></li>
              <li><Link data-testid="footer-nav-about" to="/despre" className="text-ivory/60 transition-colors hover:text-ivory">{t.nav.about}</Link></li>
              <li><Link data-testid="footer-nav-method" to="/metoda" className="text-ivory/60 transition-colors hover:text-ivory">{t.nav.method}</Link></li>
              <li><Link data-testid="footer-nav-blog" to="/blog" className="text-ivory/60 transition-colors hover:text-ivory">{t.nav.blog}</Link></li>
              <li><Link data-testid="footer-nav-contact" to="/contact" className="text-ivory/60 transition-colors hover:text-ivory">{t.nav.contact}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-bronze">{t.footer.solutionsTitle}</p>
            <ul className="mt-5 space-y-3 text-sm">
              {SOLUTIONS.map((s) => (
                <li key={s.slug}>
                  <Link data-testid={`footer-solution-${s.slug}`} to={`/solutii/${s.slug}`} className="text-ivory/60 transition-colors hover:text-ivory">
                    {s[lang].name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-bronze">{t.footer.resourcesTitle}</p>
            <ul className="mt-5 space-y-3 text-sm">
              {SEO_PAGES.map((p) => (
                <li key={p.slug}>
                  <Link data-testid={`footer-seo-${p.slug}`} to={`/${p.slug}`} className="text-ivory/60 transition-colors hover:text-ivory">
                    {p[lang].h1.split(":")[0]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-bronze">{t.footer.contactTitle}</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a data-testid="footer-phone-link" href={`tel:+4${WA_DISPLAY.replace(/\s/g, "")}`} className="flex items-center gap-2 text-ivory/60 transition-colors hover:text-ivory">
                  <Phone className="h-4 w-4 text-bronze" /> {WA_DISPLAY}
                </a>
              </li>
              <li>
                <a data-testid="footer-whatsapp-link" href={waLink(t.common.waMessage)} target="_blank" rel="noopener noreferrer" className="text-ivory/60 transition-colors hover:text-ivory">
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-coal pt-8 text-xs text-ivory/40 md:flex-row md:items-center md:justify-between">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Scutul Fermierului. {t.footer.rights}</p>
          <p data-testid="footer-legal-line" className="max-w-md md:text-center">{t.footer.legalLine}</p>
          <div className="flex gap-6">
            <Link data-testid="footer-privacy-link" to="/politica-de-confidentialitate" className="transition-colors hover:text-ivory">{t.footer.privacy}</Link>
            <Link data-testid="footer-terms-link" to="/termeni-si-conditii" className="transition-colors hover:text-ivory">{t.footer.terms}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
